// Shared by the Owners tab and the piece drawer (30 Sep, Tim: "there should be an option to add a new one", "we should be
// able to edit the owner here"). The owner ID that signs every save, the consignor picker with "New consignor…" at the
// bottom (the counter app's consignor-create: Consignify record, then our mirror), and the owner editor itself.
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { callFn, supabase } from '../../lib/supabase'
import { useMeta } from '../ReportsApp'
import { addConsignorName } from '../format'

export interface ConsignorOpt { id: string; key: string; name: string; default_split: number | null; in_consignify: boolean }
export const USUAL: Record<string, number> = { split: 47, masih: 97 }
const ID_KEY = 'starchive.reports.owner_id'
const NEW = '__new'
const label = (o: string) => o[0].toUpperCase() + o.slice(1)
export const consignorLabel = (c: { key: string; name: string }) => c.name ? `${c.name} (${c.key})` : c.key
const readId = () => { try { return sessionStorage.getItem(ID_KEY) ?? '' } catch { return '' } }

/** The owner ID that signs a save. A tap with no valid ID opens the prompt; typing a valid owner ID closes it and carries on. */
export function useOwnerSigner() {
  const { meta } = useMeta()
  const owners = meta?.owners ?? []
  const [ownerId, setState] = useState(readId)
  const idRef = useRef(ownerId)
  const [ask, setAsk] = useState(false)
  const pending = useRef<(() => void) | null>(null)
  const isOwner = (v: string) => v.length === 4 && owners.some((o) => o.employee_id === v)
  const setOwnerId = (v: string) => {
    setState(v); idRef.current = v
    try { sessionStorage.setItem(ID_KEY, v) } catch { /* ignore */ }
    if (isOwner(v) && pending.current) { const f = pending.current; pending.current = null; setAsk(false); f() }
  }
  const signer = ownerId.length === 4 ? owners.find((o) => o.employee_id === ownerId) : undefined
  /** Run fn signed: the ID typed on another screen of this tab counts too. */
  const signed = (fn: () => void) => {
    const cur = readId()
    if (cur !== idRef.current) { idRef.current = cur; setState(cur) }
    if (isOwner(cur)) fn(); else { pending.current = fn; setAsk(true) }
  }
  const askEl = ask ? createPortal(
    <div className="rp-oask" role="dialog" aria-label="Owner ID" onClick={(e) => e.stopPropagation()}>
      <div className="rp-oask__card">
        <b>Your owner ID</b>
        <span className="rp-muted">Every change here is signed. Type it once; it stays for this tab.</span>
        <input inputMode="numeric" autoFocus autoComplete="off" value={ownerId} onChange={(e) => setOwnerId(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="••••" aria-label="Owner ID" />
        {ownerId.length === 4 && !signer && <span className="rp-tone-red">Not an owner ID</span>}
        <button type="button" className="rp-btn" onClick={() => { setAsk(false); pending.current = null }}>Cancel</button>
      </div>
    </div>, document.body) : null
  return { ownerId, setOwnerId, signer, signed, idRef, askEl }
}

/** The consignor list straight from the table (the Reports session reads it like the counter app does). */
export function useConsignors(enabled = true) {
  const [list, setList] = useState<ConsignorOpt[]>([])
  useEffect(() => {
    if (!enabled) return
    let alive = true
    supabase.from('consignors').select('id, key, first_name, last_name, default_split, in_consignify').order('key').then(({ data }) => {
      if (alive && data) setList(data.map((c) => ({ id: c.id, key: c.key, name: `${c.first_name ?? ''} ${c.last_name ?? ''}`.trim(), default_split: c.default_split, in_consignify: c.in_consignify })))
    })
    return () => { alive = false }
  }, [enabled])
  return [list, setList] as const
}

/** "New consignor": the same record the counter app's Consignors screen makes. Name, phone, email, their %, paid by. */
function NewConsignor({ signer, onDone, onCancel }: { signer: ReturnType<typeof useOwnerSigner>; onDone: (c: ConsignorOpt) => void; onCancel: () => void }) {
  const [f, setF] = useState({ first_name: '', last_name: '', phone: '', email: '', pct: '80', payout_method: 'zelle', agreement: 'not_yet' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const pct = Number(f.pct)
  const ok = f.first_name.trim() && f.last_name.trim() && pct > 0 && pct < 100
  const save = () => signer.signed(async () => {
    setBusy(true); setErr('')
    try {
      const c = await callFn<{ id: string; key: string; first_name: string; last_name: string; default_split: number; in_consignify: boolean }>('consignor-create', {
        first_name: f.first_name, last_name: f.last_name, phone: f.phone, email: f.email, default_split: pct,
        payout_method: f.payout_method, payout_handle: '', unsold_fate: 'return', agreement: f.agreement, employee_id: signer.idRef.current,
      })
      const name = `${c.first_name} ${c.last_name}`.trim()
      addConsignorName(c.key, name)
      if (!c.in_consignify) setErr(`${name} saved as ${c.key}, but Consignify did not take it yet (it retries in a minute). Pieces can be given to them once it is there.`)
      onDone({ id: c.id, key: c.key, name, default_split: c.default_split, in_consignify: c.in_consignify })
    } catch (e) { setErr((e as Error).message) } finally { setBusy(false) }
  })
  const field = (k: keyof typeof f, l: string, props: React.InputHTMLAttributes<HTMLInputElement> = {}) =>
    <label className="rp-ncons__f"><span>{l}</span><input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} /></label>
  return createPortal(
    <div className="rp-oask" role="dialog" aria-label="New consignor" onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget && !busy) onCancel() }}>
      <div className="rp-oask__card rp-ncons">
        <b>New consignor</b>
        <span className="rp-muted">Added to Consignify with the next CON number, and to the consignor list on the counter app.</span>
        <div className="rp-ncons__row">{field('first_name', 'First name', { autoFocus: true, autoComplete: 'off' })}{field('last_name', 'Last name', { autoComplete: 'off' })}</div>
        <div className="rp-ncons__row">{field('phone', 'Phone', { inputMode: 'tel', autoComplete: 'off', placeholder: 'Optional' })}{field('email', 'Email', { inputMode: 'email', autoComplete: 'off', placeholder: 'Optional' })}</div>
        <div className="rp-ncons__row">
          {field('pct', 'Their % (default)', { inputMode: 'decimal', onChange: (e) => setF({ ...f, pct: e.target.value.replace(/[^0-9.]/g, '').slice(0, 5) }) })}
          <label className="rp-ncons__f"><span>Paid by</span>
            <select value={f.payout_method} onChange={(e) => setF({ ...f, payout_method: e.target.value })}>
              <option value="zelle">Zelle</option><option value="wire">Wire</option><option value="cash">Cash</option><option value="crypto">Crypto</option>
            </select>
          </label>
        </div>
        <div className="rp-oedit__seg" role="group" aria-label="Agreement">
          {([['signed', 'Agreement signed'], ['not_yet', 'Not signed yet']] as const).map(([k, l]) => <button key={k} type="button" aria-pressed={f.agreement === k} onClick={() => setF({ ...f, agreement: k })}>{l}</button>)}
        </div>
        {err && <span className="rp-tone-red">{err}</span>}
        <div className="rp-ncons__btns">
          <button type="button" className="rp-btn" disabled={busy} onClick={onCancel}>Cancel</button>
          <button type="button" className="rp-btn rp-btn--primary" disabled={!ok || busy} onClick={save}>{busy ? 'Adding…' : 'Add consignor'}</button>
        </div>
      </div>
    </div>, document.body)
}

/** The consignor dropdown with "New consignor…" last. Masih's own key (0001) is left out: his pieces are Masih or Split. */
export function ConsignorSelect({ list, value, onChange, onAdded, signer, className }: {
  list: ConsignorOpt[]; value: string; onChange: (id: string) => void; onAdded: (c: ConsignorOpt) => void; signer: ReturnType<typeof useOwnerSigner>; className?: string
}) {
  const [adding, setAdding] = useState(false)
  const opts = list.filter((c) => c.key !== '0001').sort((a, b) => (a.name || a.key).localeCompare(b.name || b.key))
  return (
    <>
      <select className={className} value={value} aria-label="Consignor" onChange={(e) => { if (e.target.value === NEW) setAdding(true); else onChange(e.target.value) }}>
        <option value="">Pick the consignor…</option>
        {opts.map((c) => <option key={c.id} value={c.id}>{consignorLabel(c)}{c.in_consignify ? '' : ' · not in Consignify yet'}</option>)}
        <option value={NEW}>+ New consignor…</option>
      </select>
      {adding && <NewConsignor signer={signer} onCancel={() => setAdding(false)} onDone={(c) => { setAdding(false); onAdded(c); onChange(c.id) }} />}
    </>
  )
}

/** Pick an owner and a share, then Save. `owners` limits the choices (the Owners card has Shane and the usual splits as
 *  their own buttons already). onSave throws to show the error here. */
export function OwnerEditor({ owners, initial, consignors, onAdded, signer, onSave, onCancel }: {
  owners: readonly ('shane' | 'masih' | 'split' | 'consignor')[]
  initial: { owner: string; consignorId: string; pct?: string }
  consignors: ConsignorOpt[]; onAdded: (c: ConsignorOpt) => void; signer: ReturnType<typeof useOwnerSigner>
  onSave: (owner: string, consignorId: string, pct: number | null) => Promise<void>; onCancel: () => void
}) {
  const [e, setE] = useState({ owner: initial.owner || owners[0], consignorId: initial.consignorId, pct: initial.pct ?? '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const pick = consignors.find((c) => c.id === e.consignorId)
  const pct = e.owner === 'shane' ? null : e.pct !== '' ? Number(e.pct) : e.owner === 'consignor' ? (pick?.default_split != null ? Number(pick.default_split) : null) : USUAL[e.owner]
  const ok = e.owner === 'shane' || (pct != null && pct > 0 && pct < 100 && (e.owner !== 'consignor' || !!pick))
  const save = () => signer.signed(async () => {
    setBusy(true); setErr('')
    try { await onSave(e.owner, e.consignorId, e.owner === 'shane' ? null : pct) } catch (x) { setErr((x as Error).message) } finally { setBusy(false) }
  })
  const who = e.owner === 'consignor' ? (pick ? pick.name || pick.key : 'consignor') : label(e.owner)
  return (
    <div className="rp-oedit">
      <div className="rp-oedit__seg" role="group" aria-label="Owner">
        {owners.map((o) => <button key={o} type="button" aria-pressed={e.owner === o} onClick={() => setE({ ...e, owner: o, pct: '' })}>{label(o)}</button>)}
      </div>
      {e.owner === 'consignor' && <ConsignorSelect list={consignors} value={e.consignorId} onChange={(id) => setE({ ...e, consignorId: id, pct: '' })} onAdded={onAdded} signer={signer} />}
      {e.owner !== 'shane' && (
        <label>{e.owner === 'consignor' ? 'Their %' : 'Masih %'}<input inputMode="decimal" value={e.pct} placeholder={e.owner === 'consignor' ? (pick?.default_split != null ? String(Number(pick.default_split)) : '…') : String(USUAL[e.owner])} onChange={(x) => setE({ ...e, pct: x.target.value.replace(/[^0-9.]/g, '').slice(0, 5) })} /></label>
      )}
      <button type="button" className="rp-ocard__ok" disabled={!ok || busy} onClick={save}>{busy ? '…' : `Save ${who}${pct != null ? ` ${pct}%` : ''}`}</button>
      <button type="button" className="rp-ocard__link" disabled={busy} onClick={onCancel}>Cancel</button>
      {e.owner === 'consignor' && pick && !pick.in_consignify && <span className="rp-tone-red rp-oedit__err">{pick.name || pick.key} is not in Consignify yet; try again in a minute.</span>}
      {err && <span className="rp-tone-red rp-oedit__err">{err}</span>}
    </div>
  )
}
